import React, { FC, useEffect, useMemo, useState } from 'react';
import { useTranslate } from '@/translations';
import { PageContainer, Title, faKey, faUser, Icon } from '@/ui/components';
import { useResetPassword } from '@/api/Users';
import {
  useMyEmailNotificationPreferences,
  useUpdateMyEmailNotificationPreferences,
} from '@/api/EmailNotifications';
import { queryClient } from '@/config/reactQuery';
import { QUERY_KEYS } from '@/api/queryKeys';
import { useAppCustomers } from '@/hooks';
import { NovuWorkflow } from '@/types';
import {
  App,
  Button,
  Checkbox,
  Col,
  Descriptions,
  Divider,
  Empty,
  Input,
  Row,
  Space,
  Spin,
  Tag,
} from 'antd';

export const ManageUserProfile: FC = () => {
  const { t } = useTranslate();
  const { message, modal } = App.useApp();

  const {
    userPermissions: { userName, userDisplayName, roles },
  } = useAppCustomers();

  const [newPassword, setNewPassword] = useState('');
  const [repeatPassword, setRepeatPassword] = useState('');
  const [enabledNotifications, setEnabledNotifications] = useState<
    NovuWorkflow[]
  >([]);

  const { mutateAsync: resetPasswordAsync, isPending: resetPasswordLoading } =
    useResetPassword();
  const { data: preferences, isFetching: preferencesLoading } =
    useMyEmailNotificationPreferences();
  const { mutateAsync: updatePreferencesAsync, isPending: savingPreferences } =
    useUpdateMyEmailNotificationPreferences();

  const selfModifiable = useMemo(
    () => (preferences || []).filter((p) => p.allowUserModify),
    [preferences],
  );
  const enabledFromPreferences = useMemo(
    () => selfModifiable.filter((p) => p.isEnabled).map((p) => p.novuWorkflow),
    [selfModifiable],
  );

  useEffect(() => {
    setEnabledNotifications((current) =>
      current.length === enabledFromPreferences.length &&
      current.every(
        (workflow, index) => workflow === enabledFromPreferences[index],
      )
        ? current
        : enabledFromPreferences,
    );
  }, [enabledFromPreferences]);

  const isLoading =
    resetPasswordLoading || preferencesLoading || savingPreferences;

  const handleResetPasswordClick = () => {
    if (newPassword.length === 0) {
      message.error('Please enter a new password');
      return;
    }

    if (newPassword !== repeatPassword) {
      message.error('Passwords do not match');
      return;
    }

    modal.confirm({
      title: 'Confirm',
      content: 'Are you sure you want to reset the password?',
      okText: t('yes'),
      onOk: async () => {
        const result = await resetPasswordAsync(newPassword);
        if (!result.success || result.data?.length) {
          message.error(
            result.data || result.message || 'Failed to reset password',
          );
          return;
        }
        message.success('Password reset successfully');
        setNewPassword('');
        setRepeatPassword('');
      },
      onCancel: () => {},
    });
  };

  const handleSavePreferences = async () => {
    const enabledSet = new Set(enabledNotifications);
    const items = selfModifiable.map((p) => ({
      novuWorkflow: p.novuWorkflow,
      isEnabled: enabledSet.has(p.novuWorkflow),
    }));
    const result = await updatePreferencesAsync(items);
    if (!result.success) {
      message.error(result.message || 'Failed to save preferences');
      return;
    }
    await queryClient.invalidateQueries({
      queryKey: [QUERY_KEYS.myEmailNotificationPreferences],
    });
    message.success(t('save'));
  };

  return (
    <Spin spinning={isLoading}>
      <PageContainer>
        <Title title={t('userProfile')} icon={faUser} />

        <Descriptions
          column={1}
          bordered
          style={{ maxWidth: 800 }}
          items={[
            {
              key: 0,
              label: t('displayName'),
              children: userDisplayName ?? '',
            },
            {
              key: 1,
              label: t('username'),
              children: userName ?? '',
            },
            {
              key: 2,
              label: t('role'),
              children: (
                <Space wrap>
                  {roles?.map((o) => (
                    <Tag key={o} className="py-1 m-0">
                      {o}
                    </Tag>
                  ))}
                </Space>
              ),
            },
            {
              key: 3,
              label: t('emailNotificationPreferences'),
              children:
                selfModifiable.length === 0 ? (
                  <Empty
                    description={t('noSelfModifiableEmailNotifications')}
                  />
                ) : (
                  <div className="d-flex align-items-center justify-content-between gap-2">
                    <Space
                      direction="vertical"
                      size="middle"
                      style={{ maxWidth: 800 }}
                    >
                      <Checkbox.Group
                        value={enabledNotifications}
                        onChange={(values) =>
                          setEnabledNotifications(values as NovuWorkflow[])
                        }
                        options={selfModifiable.map((p) => ({
                          label: p.description,
                          value: p.novuWorkflow,
                        }))}
                      />
                    </Space>
                    <Button type="primary" onClick={handleSavePreferences}>
                      {t('save')}
                    </Button>
                  </div>
                ),
            },
          ]}
        />

        <Divider />
        <Row gutter={[16, 16]}>
          <Col sm={24} lg={8} xxl={6}>
            <Input
              type="password"
              size="large"
              placeholder="Enter new password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
            />
          </Col>
          <Col sm={24} lg={8} xxl={6}>
            <Input
              type="password"
              size="large"
              placeholder="Repeat new password"
              value={repeatPassword}
              onChange={(e) => setRepeatPassword(e.target.value)}
            />
          </Col>
          <Col sm={24} lg={8} xxl={6}>
            <Button
              type="primary"
              size="large"
              icon={<Icon icon={faKey} />}
              onClick={handleResetPasswordClick}
            >
              {t('resetPassword')}
            </Button>
          </Col>
        </Row>
      </PageContainer>
    </Spin>
  );
};
